import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, shortDate } from "../lib/format";
import { toast } from "sonner";
import { CheckCircle2, Circle, HelpCircle, ChevronDown, Wallet, Info, Search, AlertTriangle, Scale } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { useAccounts } from "../lib/accounts";
import ReconcileBadge from "../components/ReconcileBadge";

/** Small numbered pill used inline next to each step header */
function StepBadge({ n }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1a1a1a] text-[#b5e4ca] font-black text-xs border-2 border-[#1a1a1a]">
      {n}
    </span>
  );
}

/** Show a mini row for a transaction suspect. Links to /register?q=... so the
 * user can find/edit it. */
function SuspectRow({ t }) {
  return (
    <Link
      to={`/txn/${t.id}`}
      data-testid={`diag-suspect-${t.id}`}
      className="flex items-center justify-between py-2 px-3 border-b-2 border-[#1a1a1a]/10 last:border-0 hover:bg-white/50"
    >
      <div className="min-w-0">
        <div className="font-bold text-sm truncate">{t.payee || "(no payee)"}</div>
        <div className="text-[11px] font-medium text-[#4a4a4a]">
          {shortDate(t.date)} · {t.category || "Other"}
          {!t.cleared && " · uncleared"}
        </div>
      </div>
      <div
        className={`font-display font-black text-sm ${t.type === "deposit" ? "balance-pos" : ""}`}
      >
        {t.type === "deposit" ? "+" : "-"}
        {fmtMoney(t.amount)}
      </div>
    </Link>
  );
}

function DiagSection({ title, hint, items, emptyText, testid, renderer }) {
  const isEmpty = items.length === 0;
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b-2 border-[#1a1a1a]/10 bg-[#f4f0eb]">
        <div className="font-display font-black text-sm flex items-center gap-1.5">
          <AlertTriangle size={13} strokeWidth={3} />
          {title}
          <span className="ml-auto text-[11px] font-bold text-[#4a4a4a]">
            {items.length}
          </span>
        </div>
        <div className="text-[11px] font-medium text-[#4a4a4a] mt-0.5">{hint}</div>
      </div>
      {isEmpty ? (
        <div className="p-3 text-[11px] font-medium text-[#4a4a4a] italic">
          {emptyText}
        </div>
      ) : (
        items.map(renderer)
      )}
    </div>
  );
}

function ReconcileDiagnosticPanel({ data, onClose }) {
  const {
    difference,
    abs_difference,
    period_totals,
    amount_match,
    double_amount,
    stale_uncleared,
    fuzzy_duplicates,
  } = data;

  return (
    <div className="mt-3 space-y-2" data-testid="diagnostic-panel">
      <div className="nb-card p-3 flex items-start gap-2 bg-[#fdecc8]">
        <Search size={14} strokeWidth={3} className="mt-0.5" />
        <div className="text-[11px] font-medium flex-1">
          <div className="font-black text-sm">
            Diagnostic — off by {fmtMoney(difference)}
          </div>
          <div className="mt-0.5">
            Deposits cleared:{" "}
            <span className="font-bold">{fmtMoney(period_totals.deposits)}</span> ·
            withdrawals cleared:{" "}
            <span className="font-bold">{fmtMoney(period_totals.withdrawals)}</span>{" "}
            ({period_totals.cleared_count} txns).
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="diag-close"
          className="text-xs font-bold underline text-[#4a4a4a]"
        >
          Close
        </button>
      </div>

      <DiagSection
        testid="diag-amount-match"
        title="Wrong-amount candidates"
        hint={`Transactions whose amount = ${fmtMoney(abs_difference)}. Missing or extra one of these usually explains the difference.`}
        items={amount_match}
        emptyText="No txn matches the difference exactly."
        renderer={(t) => <SuspectRow key={t.id} t={t} />}
      />

      <DiagSection
        testid="diag-double-amount"
        title="Deposit/withdrawal flip candidates"
        hint={`Amount = ${fmtMoney(abs_difference / 2)}. If one of these was entered as the wrong type (deposit vs withdrawal), the flip is worth 2× its amount.`}
        items={double_amount}
        emptyText="No half-difference txn found."
        renderer={(t) => <SuspectRow key={t.id} t={t} />}
      />

      <DiagSection
        testid="diag-fuzzy-dupes"
        title="Possible duplicates"
        hint="Pairs with the same amount + type dated within 14 days. Extra duplicate = one to delete."
        items={fuzzy_duplicates}
        emptyText="No fuzzy duplicates found."
        renderer={(pair, i) => (
          <div
            key={`${pair.a.id}-${pair.b.id}`}
            data-testid={`diag-dupe-${i}`}
            className="border-b-2 border-[#1a1a1a]/10 last:border-0"
          >
            <SuspectRow t={pair.a} />
            <SuspectRow t={pair.b} />
          </div>
        )}
      />

      <DiagSection
        testid="diag-stale-uncleared"
        title="Old uncleared (>30 days)"
        hint="Stale outstanding items. If the bank actually processed one of these but it's still marked uncleared, that's your difference."
        items={stale_uncleared}
        emptyText="No stale uncleared items."
        renderer={(t) => <SuspectRow key={t.id} t={t} />}
      />
    </div>
  );
}

/** Slide-out walkthrough drawer */
function ReconcileHelp() {
  const [openNoStmt, setOpenNoStmt] = useState(false);
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="reconcile-help-btn"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
          title="How does reconcile work?"
        >
          <HelpCircle size={16} strokeWidth={3} />
          <span className="hidden sm:inline">Help</span>
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        data-testid="reconcile-help-drawer"
        className="overflow-y-auto sm:max-w-md w-11/12 p-0 bg-[#f4f0eb] border-l-2 border-[#1a1a1a]"
      >
        <SheetHeader className="p-5 border-b-2 border-[#1a1a1a] bg-[#d9d4f1]">
          <SheetTitle className="font-display font-black text-2xl text-[#1a1a1a] flex items-center gap-2">
            <HelpCircle size={22} strokeWidth={3} /> How to reconcile
          </SheetTitle>
          <p className="text-xs font-bold text-[#4a4a4a] text-left">
            Reconciling means: make sure what SyncBook shows matches what the bank shows.
          </p>
        </SheetHeader>

        <div className="p-5 space-y-5 text-sm">
          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge n={1} />
              <h3 className="font-display font-black text-base">Pick the right account</h3>
            </div>
            <p className="font-medium text-[#4a4a4a]">
              At the top of the app you&apos;ll see chips for each bank account (Us Checking,
              Overflow, etc.). Reconciling works <b>one account at a time</b> — tap the one your
              statement is for.
            </p>
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge n={2} />
              <h3 className="font-display font-black text-base">Type the ending balance</h3>
            </div>
            <p className="font-medium text-[#4a4a4a]">
              Enter the <b>ending balance</b> from your paper/PDF statement into the big field.
              (If you&apos;re using today&apos;s bank app, use its <b>current available balance</b>.)
            </p>
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge n={3} />
              <h3 className="font-display font-black text-base">Check off cleared items</h3>
            </div>
            <p className="font-medium text-[#4a4a4a]">
              Scroll the <em>Tap to mark cleared</em> list and tap every transaction that already
              shows on the statement (or in the bank&apos;s posted-transactions list).
            </p>
            <ul className="pl-4 list-disc space-y-1 font-medium text-[#4a4a4a]">
              <li><b>Green ✔</b> = cleared. The bank processed it.</li>
              <li><b>Yellow</b> = still pending — leave it alone.</li>
            </ul>
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-2">
              <StepBadge n={4} />
              <h3 className="font-display font-black text-base">Read the &quot;Difference&quot; tile</h3>
            </div>
            <ul className="pl-4 list-disc space-y-1 font-medium text-[#4a4a4a]">
              <li>
                <b>$0.00 (green)</b> → everything ties out. Tap <b>Balanced — save</b>. 🎯
              </li>
              <li>
                <b>Not zero (red)</b> → common causes:
                <ul className="pl-4 list-disc mt-1">
                  <li>Bank shows a charge not in SyncBook → tap <b>+ New</b> to add it.</li>
                  <li>SyncBook has something bank doesn&apos;t → leave uncleared, it&apos;s pending.</li>
                  <li>Wrong amount → open the txn, fix, come back.</li>
                  <li>Missing bank fee → add it as a withdrawal.</li>
                </ul>
              </li>
            </ul>
          </section>

          <section className="space-y-2 pt-2 border-t-2 border-[#1a1a1a]/20">
            <div className="flex items-center gap-2">
              <Info size={18} strokeWidth={3} />
              <h3 className="font-display font-black text-base">Shortcut: import your CSV</h3>
            </div>
            <p className="font-medium text-[#4a4a4a]">
              Most banks let you download a CSV of your statement. Tap{" "}
              <b>📄 Import bank CSV</b> above, pick the file + target account, and SyncBook
              auto-matches everything for you. Come back here — most rows will already be green.
            </p>
          </section>

          {/* Collapsible: no statement */}
          <section className="border-2 border-[#1a1a1a] rounded-xl bg-[#fdecc8] overflow-hidden">
            <button
              data-testid="no-statement-toggle"
              onClick={() => setOpenNoStmt(!openNoStmt)}
              className="w-full p-3 flex items-center justify-between font-display font-black text-left"
            >
              <span>🚨 No bank statement? Read this</span>
              <ChevronDown
                size={18}
                strokeWidth={3}
                className={`transition ${openNoStmt ? "rotate-180" : ""}`}
              />
            </button>
            {openNoStmt && (
              <div className="p-4 pt-0 space-y-3 text-sm font-medium text-[#1a1a1a]">
                <div>
                  <div className="font-black">Option A — Use the bank&apos;s app (best)</div>
                  <ol className="pl-5 list-decimal space-y-0.5 mt-1">
                    <li>Log into your bank on your phone.</li>
                    <li>Note the <b>current available balance</b>.</li>
                    <li>Type it into the Bank balance field here.</li>
                    <li>Tap ✔ on every SyncBook transaction that&apos;s on the bank&apos;s posted list.</li>
                    <li>Pending charges → leave uncleared.</li>
                  </ol>
                </div>
                <div>
                  <div className="font-black">Option B — Download today&apos;s CSV</div>
                  <p className="mt-1">
                    Most bank apps let you export activity even for the current (open) period. Grab
                    a CSV, then use the <b>Import bank CSV</b> shortcut.
                  </p>
                </div>
                <div>
                  <div className="font-black">Option C — Sanity check only</div>
                  <p className="mt-1">
                    Leave the Bank balance field empty. Tap ✔ on transactions you&apos;re certain
                    have cleared (paycheck last week, monthly-1st subscription, etc.), tap{" "}
                    <b>Save cleared state</b>, come back tomorrow to finish.
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="space-y-1.5 pt-2 border-t-2 border-[#1a1a1a]/20">
            <h3 className="font-display font-black text-base">Pro tips</h3>
            <ul className="pl-4 list-disc space-y-1 font-medium text-[#4a4a4a]">
              <li>Reconcile monthly, not daily — pending charges muddle things.</li>
              <li>Never edit a cleared transaction&apos;s amount without unclearing first.</li>
              <li>
                Difference stuck at an odd number ($25, $2.50)? Usually a bank fee not yet in
                SyncBook — add it.
              </li>
              <li>Ran a CSV twice by mistake? Use 📥 Review duplicate inbox on the Register page.</li>
            </ul>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const _RECONCILE_MARKER = "reconcile"; // no-op sentinel; keeps module-scope clean

export default function Reconcile() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [bankBalance, setBankBalance] = useState("");
  const [pendingCleared, setPendingCleared] = useState({});
  const { current, selectedId } = useAccounts();
  const [latestReconciles, setLatestReconciles] = useState({});

  const load = async () => {
    const acc = selectedId ? `?account_id=${selectedId}` : "";
    const [s, t, r] = await Promise.all([
      api.get(`/summary${acc}`),
      api.get(`/transactions${acc}`),
      api.get("/reconcile/latest"),
    ]);
    setSummary(s.data);
    setTxns(t.data);
    setLatestReconciles(r.data || {});
    const p = {};
    t.data.forEach((x) => (p[x.id] = x.cleared));
    setPendingCleared(p);
    setBankBalance("");
  };

  useEffect(() => {
    load();
  }, [selectedId]);

  const clearedTotal = useMemo(() => {
    const start = summary?.starting_balance ?? 0;
    let total = start;
    for (const t of txns) {
      if (pendingCleared[t.id]) {
        total += t.type === "deposit" ? t.amount : -t.amount;
      }
    }
    return total;
  }, [pendingCleared, txns, summary]);

  const difference = bankBalance === "" ? null : parseFloat(bankBalance) - clearedTotal;
  const balanced = difference !== null && Math.abs(difference) < 0.005;

  const [diagnostic, setDiagnostic] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiagnostic = async () => {
    if (bankBalance === "") {
      toast.error("Enter the bank balance first");
      return;
    }
    setDiagBusy(true);
    try {
      const acc = selectedId ? `&account_id=${selectedId}` : "";
      const { data } = await api.get(
        `/reconcile/diagnostic?bank_balance=${encodeURIComponent(bankBalance)}${acc}`
      );
      setDiagnostic(data);
      const hits =
        data.amount_match.length +
        data.double_amount.length +
        data.stale_uncleared.length +
        data.fuzzy_duplicates.length;
      toast.success(
        hits === 0
          ? "No obvious suspects — check bank fees / period totals below"
          : `Found ${hits} suspect${hits === 1 ? "" : "s"} to investigate`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const toggle = (id) => setPendingCleared((p) => ({ ...p, [id]: !p[id] }));

  const save = async () => {
    if (bankBalance === "") return toast.error("Enter bank balance first");
    const cleared_ids = Object.entries(pendingCleared).filter(([, v]) => v).map(([k]) => k);
    await Promise.all(
      txns
        .map((t) =>
          pendingCleared[t.id] !== t.cleared
            ? api.put(`/transactions/${t.id}`, { cleared: !!pendingCleared[t.id] })
            : null
        )
        .filter(Boolean)
    );
    const { data } = await api.post("/reconcile", {
      bank_balance: parseFloat(bankBalance),
      cleared_ids,
      account_id: selectedId || undefined,
    });
    if (data.balanced) {
      toast.success("Balanced! You're good. 🎯");
    } else {
      toast(`Off by ${fmtMoney(data.difference)}`);
    }
    load();
  };

  return (
    <div className="space-y-4" data-testid="reconcile-page">
      {/* Header with help */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-black">Reconcile</h1>
        <ReconcileHelp />
      </div>

      {/* Step 1 hint — pick account */}
      <div
        className="nb-card p-3 flex items-start gap-2"
        style={{ background: "#e5f0ff" }}
        data-testid="reconcile-step-1"
      >
        <StepBadge n={1} />
        <div className="text-sm font-medium flex-1">
          <div>
            <span className="font-black">Reconciling </span>
            <span
              className="inline-flex items-center gap-1 chip !py-0.5 !text-xs"
              style={{ background: current?.color || "#fff" }}
            >
              <Wallet size={12} strokeWidth={3} />
              {current?.name || "your account"}
            </span>
            . Wrong one? Tap another chip at the top of the app.
          </div>
          <div className="mt-1.5">
            <ReconcileBadge
              latest={selectedId ? latestReconciles[selectedId] : null}
              testid="reconcile-badge-current"
            />
          </div>
        </div>
      </div>

      <Link to="/import" data-testid="import-csv-link" className="nb-btn w-full !py-3">
        📄 Import bank CSV (auto-match everything)
      </Link>

      <Link
        to="/reconcile/csv-diff"
        data-testid="csv-diff-link"
        className="nb-btn nb-btn-secondary w-full !py-3 flex items-center justify-center gap-2"
      >
        <Search size={16} strokeWidth={3} />
        Compare bank CSV vs SyncBook (find missing rows)
      </Link>

      <div className="nb-card-lg p-5 space-y-4">
        {/* Step 2 hint — bank balance */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StepBadge n={2} />
            <label className="label !mb-0">Bank statement balance</label>
          </div>
          <p className="text-[11px] font-medium text-[#4a4a4a] mb-2 pl-8">
            The <b>ending balance</b> from your statement — or your bank app&apos;s current balance
            if you don&apos;t have one.{" "}
            <span className="italic">No statement? Tap Help (top right).</span>
          </p>
          <input
            data-testid="bank-balance-input"
            type="number"
            step="0.01"
            className="nb-input text-2xl font-display font-black"
            value={bankBalance}
            onChange={(e) => setBankBalance(e.target.value)}
            placeholder="0.00"
          />
        </div>

        {/* Step 4 hint — the difference tile */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <StepBadge n={4} />
            <div className="label !mb-0">Watch the difference</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-3 border-2 border-[#1a1a1a] rounded-xl bg-white">
              <div className="label !mb-1">Cleared</div>
              <div data-testid="cleared-total" className="font-display font-black text-lg">
                {fmtMoney(clearedTotal)}
              </div>
            </div>
            <div
              className="p-3 border-2 border-[#1a1a1a] rounded-xl"
              style={{
                background:
                  difference === null
                    ? "#fff"
                    : balanced
                      ? "var(--positive)"
                      : "var(--danger)",
              }}
            >
              <div className="label !mb-1">Difference</div>
              <div data-testid="difference" className="font-display font-black text-lg">
                {difference === null ? "—" : fmtMoney(difference)}
              </div>
            </div>
            <div className="p-3 border-2 border-[#1a1a1a] rounded-xl bg-white">
              <div className="label !mb-1">Bank</div>
              <div className="font-display font-black text-lg">
                {bankBalance === "" ? "—" : fmtMoney(parseFloat(bankBalance))}
              </div>
            </div>
          </div>
          {difference !== null && !balanced && (
            <p className="text-[11px] font-medium text-[#4a4a4a] mt-2 pl-8">
              Off by <b>{fmtMoney(difference)}</b>. Usually a bank fee, a missed charge, or a
              pending item. Tap Help for the full checklist.
            </p>
          )}
        </div>

        {/* "Still stuck?" — collapsible power tools. Only revealed when the
            reconcile isn't balanced, so first-time users see a clean flow. */}
        {difference !== null && !balanced && (
          <details className="nb-card p-0 overflow-hidden" data-testid="still-stuck">
            <summary
              data-testid="still-stuck-toggle"
              className="p-3 cursor-pointer list-none flex items-center justify-between gap-2 font-display font-black text-sm"
            >
              <span>🔎 Still stuck? Advanced tools</span>
              <ChevronDown size={16} strokeWidth={3} />
            </summary>
            <div className="p-3 pt-0 space-y-3 border-t-2 border-[#1a1a1a]/10">
              <div className="space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Reconcile diagnostic
                </div>
                <button
                  type="button"
                  onClick={runDiagnostic}
                  disabled={diagBusy}
                  data-testid="run-diagnostic-btn"
                  className="nb-btn nb-btn-secondary w-full !py-2 text-sm"
                >
                  <Search size={14} strokeWidth={3} />
                  {diagBusy ? "Scanning…" : "Categorize the discrepancy"}
                </button>
                {diagnostic && (
                  <ReconcileDiagnosticPanel
                    data={diagnostic}
                    onClose={() => setDiagnostic(null)}
                  />
                )}
              </div>
            </div>
          </details>
        )}

        <button
          data-testid="save-reconcile-btn"
          onClick={save}
          className={`nb-btn w-full !py-3 ${balanced ? "nb-btn-positive" : "nb-btn-dark"}`}
        >
          {balanced ? "Balanced — save 🎯" : "Save cleared state"}
        </button>
      </div>

      {/* Step 3 — check off cleared */}
      <div className="nb-card overflow-hidden">
        <div className="p-4 border-b-2 border-[#1a1a1a]/10 flex items-center gap-2">
          <StepBadge n={3} />
          <div>
            <div className="label !mb-0">Tap to mark cleared</div>
            <div className="text-[11px] font-medium text-[#4a4a4a]">
              Green ✔ = shows on your statement. Yellow = still pending.
            </div>
          </div>
        </div>
        {txns.length === 0 && (
          <div className="p-6 text-sm text-center text-[#4a4a4a] font-medium">
            No transactions to reconcile.
          </div>
        )}
        {txns.map((t) => (
          <button
            key={t.id}
            data-testid={`reconcile-row-${t.id}`}
            onClick={() => toggle(t.id)}
            className="w-full flex items-center justify-between py-3 px-4 border-b-2 border-[#1a1a1a]/10 last:border-0 text-left transition"
            style={{ background: pendingCleared[t.id] ? "var(--positive)" : "var(--uncleared)" }}
          >
            <div className="flex items-center gap-3 min-w-0">
              {pendingCleared[t.id] ? (
                <CheckCircle2 size={22} strokeWidth={3} />
              ) : (
                <Circle size={22} strokeWidth={3} />
              )}
              <div className="min-w-0">
                <div className="font-bold truncate">{t.payee}</div>
                <div className="text-xs text-[#4a4a4a] font-medium">{shortDate(t.date)}</div>
              </div>
            </div>
            <div
              className={`font-display font-black text-lg ${t.type === "deposit" ? "balance-pos" : ""}`}
            >
              {t.type === "deposit" ? "+" : "-"}
              {fmtMoney(t.amount)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
